// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Process Characterization Sheet Panel
// Panel 2 del Explorer Workspace
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, type CSSProperties, type JSX } from 'react';
import { C, TIPOLOGIA_CONFIG } from '../../../../lib/design';
import { Ic, ICON_PATHS, StatusBadge, DocTypeBadge } from '../../../../lib/ui';
import {
  fetchDocumentos,
  crearDocumento,
  subirArchivo,
  fetchFicha,
  actualizarFicha,
} from '../../../shared/services/api';
import { ArchivosPanel } from '../docs/components/ArchivosPanel';
import { FONTS } from '../../../../config/documentStyles';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import type { Proceso, DocumentoResumen, TipoDocumental, TipoContenido } from '../../../../types';

// ─── Props ───

interface ProcessSheetPanelProps {
  proceso: Proceso | null;
  onSelectDocumento?: (doc: DocumentoResumen) => void;
  twoColumnLayout?: boolean;
  // Lifted editing state for unified header
  isEditing?: boolean;
  onToggleEdit?: () => void;
  /** Show only documentation cards (5-7), hiding identification/description cards (1-4) */
  showOnlyDocs?: boolean;
}

// ─── Draft doc type ───

interface DraftDoc {
  id: string;
  nombre: string;
  tipo: TipoDocumental;
}

// ─── Mock data ───

interface EntradaSalida {
  texto: string;
  vinculo: string;
}

interface Indicador {
  nombre: string;
  objetivo: string;
  frecuencia: string;
  valor: number;
  meta: number;
}

// ─── TipoDocumental options for dropdown ───

const TIPO_DOCUMENTAL_CODES: TipoDocumental[] = [
  'PR',
  'NC',
  'IT',
  'MN',
  'PA',
  'RE',
  'FC',
  'FL',
  'HE',
  'MO',
  'PT',
];

// ─── Styles ───

const CARD: CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #d5d5d5',
  borderTop: '3px solid #0E243E',
  marginBottom: 0,
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  transition: 'box-shadow 0.15s',
};

const CARD_HEADER: CSSProperties = {
  padding: '12px 24px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: '#0E243E',
  borderBottom: '1px solid #E8E8E8',
  background: '#FAFBFC',
};

const CARD_BODY: CSSProperties = {
  padding: '20px 24px',
};

const LABEL: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: C.textMut,
  marginBottom: 2,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const VALUE: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: C.text,
};

const INPUT_STYLE: CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: 13,
  fontFamily: FONTS.ui,
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  color: C.text,
  background: C.surface,
};

const TEXTAREA_STYLE: CSSProperties = {
  ...INPUT_STYLE,
  minHeight: 80,
  resize: 'vertical' as const,
};

// ─── Section header icons (celeste #33BCE1) ──────────────────────────────────

const SI = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: '#33BCE1',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};
const SECTION_ICONS: Record<string, JSX.Element> = {
  identificacion: (
    <svg {...SI}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      <path d="M3 9h6" />
    </svg>
  ),
  descripcion: (
    <svg {...SI}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </svg>
  ),
  entradas: (
    <svg {...SI}>
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  ),
  reportes: (
    <svg {...SI}>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  documentacion: (
    <svg {...SI}>
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
  herramientas: (
    <svg {...SI}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  ),
  indicadores: (
    <svg {...SI}>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
      <circle cx="18" cy="10" r="2" />
      <circle cx="12" cy="4" r="2" />
      <circle cx="6" cy="14" r="2" />
    </svg>
  ),
};

const HEADER_WITH_ICON: CSSProperties = {
  ...CARD_HEADER,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const CHIP: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 10px',
  borderRadius: 16,
  fontSize: 12,
  fontWeight: 500,
  background: `${C.azul2}12`,
  color: C.azul2,
  border: `1px solid ${C.azul2}25`,
};

const BTN_PRIMARY: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 14px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: FONTS.ui,
  border: 'none',
  cursor: 'pointer',
  background: C.azul2,
  color: '#fff',
};

const BTN_OUTLINE: CSSProperties = {
  ...BTN_PRIMARY,
  background: 'transparent',
  color: C.textSec,
  border: `1px solid ${C.border}`,
};

const BTN_SMALL: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: 6,
  border: `1px solid ${C.border}`,
  background: C.surface,
  cursor: 'pointer',
  padding: 0,
  color: C.textSec,
};

// ─── Component ───

export function ProcessSheetPanel({
  proceso,
  onSelectDocumento,
  twoColumnLayout,
  isEditing: liftedIsEditing,
  onToggleEdit,
  showOnlyDocs = false,
}: ProcessSheetPanelProps) {
  const { t } = useI18n();
  // Internal editing state (used when not lifted)
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = liftedIsEditing !== undefined ? liftedIsEditing : internalIsEditing;
  const hasLiftedEdit = liftedIsEditing !== undefined && onToggleEdit !== undefined;

  const [documentos, setDocumentos] = useState<DocumentoResumen[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Draft documents (not yet in DB)
  const [draftDocs, setDraftDocs] = useState<DraftDoc[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [newDocTipo, setNewDocTipo] = useState<TipoDocumental>('NC');
  const [newDocTipoContenido, setNewDocTipoContenido] = useState<TipoContenido>('DIGITAL');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [enlaceUrl, setEnlaceUrl] = useState('');
  const [enlaceMime, setEnlaceMime] = useState('application/pdf');
  const [creatingDocId, setCreatingDocId] = useState<string | null>(null);

  // Expanded archivos row
  const [expandedArchivosDocId, setExpandedArchivosDocId] = useState<string | null>(null);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

  // Ficha data from API
  const [, setFichaLoaded] = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const [involucrados, setInvolucrados] = useState<string[]>([]);
  const [alcance, setAlcance] = useState('');
  const [entradas, setEntradas] = useState<EntradaSalida[]>([]);
  const [salidas, setSalidas] = useState<EntradaSalida[]>([]);
  const [reportes, setReportes] = useState<string[]>([]);
  const [requisitos, setRequisitos] = useState<string[]>([]);
  const [herramientas, setHerramientas] = useState<string[]>([]);
  const [indicadores, setIndicadores] = useState<Indicador[]>([]);
  const [snapshot, setSnapshot] = useState<any>(null);

  // Fetch documentos when proceso changes
  const loadDocumentos = useCallback(() => {
    if (!proceso) {
      setDocumentos([]);
      return;
    }
    setLoadingDocs(true);
    fetchDocumentos({ procesoId: proceso.id, porPagina: '100' })
      .then((res) => setDocumentos(res.datos ?? []))
      .catch(() => setDocumentos([]))
      .finally(() => setLoadingDocs(false));
  }, [proceso?.id]);

  useEffect(() => {
    loadDocumentos();
  }, [loadDocumentos]);

  // Fetch ficha data when proceso changes
  useEffect(() => {
    if (!proceso) {
      setFichaLoaded(false);
      return;
    }
    if (!hasLiftedEdit) setInternalIsEditing(false);
    setFichaLoaded(false);
    setDraftDocs([]);
    setShowAddForm(false);
    fetchFicha(proceso.id)
      .then((res) => {
        const f = res.data;
        setInvolucrados(f.involucrados ?? []);
        setAlcance(f.alcance ?? '');
        setDescripcion(proceso.descripcion ?? '');
        setEntradas(
          (f.entradas ?? []).map((e: Record<string, string>) => ({
            texto: e.texto ?? e.value ?? '',
            vinculo: e.vinculo ?? e.dir ?? '',
          })),
        );
        setSalidas(
          (f.salidas ?? []).map((s: Record<string, string>) => ({
            texto: s.texto ?? s.value ?? '',
            vinculo: s.vinculo ?? s.dir ?? '',
          })),
        );
        setReportes(f.reportes ?? []);
        setRequisitos(f.requisitos ?? []);
        setHerramientas(f.herramientas ?? []);
        setIndicadores(
          (f.indicadores ?? []).map((i: Record<string, unknown>) => ({
            nombre: (i.nombre ?? '') as string,
            objetivo: (i.objetivo ?? i.descripcion ?? '') as string,
            frecuencia: (i.frecuencia ?? i.unidad ?? '') as string,
            valor: Number(i.valor ?? i.valorObjetivo ?? 0),
            meta: Number(i.meta ?? 0),
          })),
        );
        setSnapshot({
          involucrados: f.involucrados ?? [],
          alcance: f.alcance ?? '',
          descripcion: proceso.descripcion ?? '',
          entradas: f.entradas ?? [],
          salidas: f.salidas ?? [],
          reportes: f.reportes ?? [],
          requisitos: f.requisitos ?? [],
          herramientas: f.herramientas ?? [],
          indicadores: f.indicadores ?? [],
        });
        setFichaLoaded(true);
      })
      .catch(() => {
        // If API fails, use empty defaults
        setDescripcion(proceso.descripcion ?? '');
        setFichaLoaded(true);
      });
  }, [proceso?.id]);

  const handleCancel = useCallback(() => {
    if (snapshot) {
      setInvolucrados(snapshot.involucrados);
      setAlcance(snapshot.alcance);
      setDescripcion(snapshot.descripcion);
      setEntradas(snapshot.entradas);
      setSalidas(snapshot.salidas);
      setReportes(snapshot.reportes);
      setRequisitos(snapshot.requisitos);
      setHerramientas(snapshot.herramientas);
      setIndicadores(snapshot.indicadores);
    }
    if (!hasLiftedEdit) setInternalIsEditing(false);
  }, [snapshot, hasLiftedEdit]);

  const handleSave = useCallback(async () => {
    if (!proceso) return;
    try {
      await actualizarFicha(proceso.id, {
        involucrados,
        alcance,
        entradas,
        salidas,
        reportes,
        requisitos,
        herramientas,
        indicadores,
      });
      // Update snapshot to reflect saved state
      setSnapshot({
        involucrados,
        alcance,
        descripcion,
        entradas,
        salidas,
        reportes,
        requisitos,
        herramientas,
        indicadores,
      });
    } catch (err) {
      console.error('Error guardando ficha:', err);
    }
    if (!hasLiftedEdit) setInternalIsEditing(false);
  }, [
    proceso,
    involucrados,
    alcance,
    descripcion,
    entradas,
    salidas,
    reportes,
    requisitos,
    herramientas,
    indicadores,
    hasLiftedEdit,
  ]);

  // ─── Draft doc handlers ───

  const handleAddDraft = useCallback(() => {
    if (!newDocName.trim()) return;
    const draft: DraftDoc = {
      id: crypto.randomUUID(),
      nombre: newDocName.trim(),
      tipo: newDocTipo,
    };
    setDraftDocs((prev) => [...prev, draft]);
    setNewDocName('');
    setNewDocTipo('NC');
    setShowAddForm(false);
  }, [newDocName, newDocTipo]);

  const handleRemoveDraft = useCallback((id: string) => {
    setDraftDocs((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const handleCreateDoc = useCallback(
    async (draft: DraftDoc) => {
      if (!proceso) return;
      setCreatingDocId(draft.id);
      try {
        const createBody: Parameters<typeof crearDocumento>[0] = {
          procesoId: proceso.id,
          titulo: draft.nombre,
          tipoDocumental: draft.tipo,
          tipoContenido: newDocTipoContenido,
        };

        if (newDocTipoContenido === 'ENLACE_EXTERNO') {
          createBody.enlaceExterno = enlaceUrl;
          createBody.nombreArchivo = draft.nombre;
          createBody.mimeType = enlaceMime;
        }

        const result = await crearDocumento(createBody);

        // For ARCHIVO: upload file after document creation
        if (newDocTipoContenido === 'ARCHIVO' && selectedFile && result.data) {
          try {
            await subirArchivo(result.data.codigo, selectedFile);
          } catch (uploadErr) {
            console.error('Error uploading file:', uploadErr);
          }
        }

        setDraftDocs((prev) => prev.filter((d) => d.id !== draft.id));
        setSelectedFile(null);
        setEnlaceUrl('');
        setEnlaceMime('application/pdf');
        setNewDocTipoContenido('DIGITAL');
        loadDocumentos();
        if (result.data && onSelectDocumento) {
          onSelectDocumento(result.data);
        }
      } catch (err) {
        console.error('Error creating document:', err);
      } finally {
        setCreatingDocId(null);
      }
    },
    [
      proceso?.id,
      loadDocumentos,
      onSelectDocumento,
      newDocTipoContenido,
      selectedFile,
      enlaceUrl,
      enlaceMime,
    ],
  );

  // ─── Placeholder ───
  if (!proceso) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: C.textMut,
          fontSize: 14,
          fontFamily: FONTS.ui,
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <Ic d={ICON_PATHS.meta} s={40} c={C.border} />
        <span>{t.process.seleccioneProcesoFicha}</span>
      </div>
    );
  }

  const tipoCfg = TIPOLOGIA_CONFIG[proceso.tipologia];

  // ─── Helper: removable list item ───
  function EditableListItem({ text, onRemove }: { text: string; onRemove: () => void }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ flex: 1, fontSize: 13, color: C.text }}>{text}</span>
        {isEditing && (
          <button
            onClick={onRemove}
            style={{ ...BTN_SMALL, width: 20, height: 20 }}
            title={t.common.eliminar}
          >
            <Ic d={ICON_PATHS.x} s={12} c={C.textMut} />
          </button>
        )}
      </div>
    );
  }

  // ─── Helper: add item prompt ───
  function AddButton({ onAdd }: { onAdd: () => void }) {
    if (!isEditing) return null;
    return (
      <button onClick={onAdd} style={{ ...BTN_SMALL, marginTop: 6 }} title={t.process.anadir}>
        <Ic d={ICON_PATHS.plus} s={14} c={C.azul2} />
      </button>
    );
  }

  // ─── Helper: entrada/salida list ───
  function IOList({
    items,
    setItems,
    borderColor,
    dirLabel,
  }: {
    items: EntradaSalida[];
    setItems: (v: EntradaSalida[]) => void;
    borderColor: string;
    dirLabel: string;
  }) {
    return (
      <div
        style={{ borderLeft: `3px solid ${borderColor}`, paddingLeft: 12, flex: 1, minWidth: 0 }}
      >
        {items.map((item, i) => (
          <div
            key={i}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8 }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{item.texto}</div>
              <div style={{ fontSize: 11, color: C.textMut }}>
                {dirLabel}: <span style={{ fontWeight: 600, color: C.azul2 }}>{item.vinculo}</span>
              </div>
            </div>
            {isEditing && (
              <button
                onClick={() => setItems(items.filter((_, j) => j !== i))}
                style={{ ...BTN_SMALL, width: 20, height: 20, flexShrink: 0, marginTop: 2 }}
                title={t.common.eliminar}
              >
                <Ic d={ICON_PATHS.x} s={12} c={C.textMut} />
              </button>
            )}
          </div>
        ))}
        {isEditing && (
          <button
            onClick={() => {
              const texto = prompt(t.process.textoEntrada);
              const vinculo = prompt(`${dirLabel}:`);
              if (texto && vinculo) setItems([...items, { texto, vinculo }]);
            }}
            style={{ ...BTN_SMALL, marginTop: 4 }}
            title={t.process.anadir}
          >
            <Ic d={ICON_PATHS.plus} s={14} c={C.azul2} />
          </button>
        )}
      </div>
    );
  }

  // ═══ CARD 1: Identificación ═══
  const card1 = (
    <div key="identificacion" style={CARD}>
      <div style={HEADER_WITH_ICON}>
        {SECTION_ICONS.identificacion}
        {t.process.identificacion}
      </div>
      <div style={CARD_BODY}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 32px' }}>
          <div>
            <div style={LABEL}>{t.processSheet.fields.code}</div>
            <div style={{ ...VALUE, fontFamily: FONTS.mono }}>{proceso.codigo}</div>
          </div>
          <div>
            <div style={LABEL}>{t.processSheet.fields.group}</div>
            <div style={VALUE}>{proceso.grupoFuncional ?? '—'}</div>
          </div>
          <div>
            <div style={LABEL}>{t.processSheet.fields.name}</div>
            <div style={VALUE}>{proceso.nombre}</div>
          </div>
          <div>
            <div style={LABEL}>{t.process.direccionResponsable}</div>
            <div style={VALUE}>
              {proceso.direccionResponsable
                ? `${proceso.direccionResponsable.puesto.titulo}${proceso.direccionResponsable.puesto.codigo ? ` (${proceso.direccionResponsable.puesto.codigo})` : ''}`
                : '—'}
            </div>
          </div>
          <div>
            <div style={LABEL}>{t.processSheet.fields.type}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: tipoCfg.color,
                }}
              />
              <span style={VALUE}>
                {proceso.tipologia} — {tipoCfg.label}
              </span>
            </div>
          </div>
          <div>
            <div style={LABEL}>{t.process.representanteProceso}</div>
            <div style={VALUE}>
              {proceso.representante?.persona
                ? `${proceso.representante.persona.nombre} ${proceso.representante.persona.apellidos}`
                : (proceso.representante?.puesto.titulo ?? '—')}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={LABEL}>{t.processSheet.fields.involved}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {involucrados.map((inv, i) => (
              <span key={i} style={CHIP}>
                {inv}
                {isEditing && (
                  <button
                    onClick={() => setInvolucrados(involucrados.filter((_, j) => j !== i))}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      marginLeft: 2,
                    }}
                  >
                    <Ic d={ICON_PATHS.x} s={12} c={C.azul2} />
                  </button>
                )}
              </span>
            ))}
            {isEditing && (
              <button
                onClick={() => {
                  const v = prompt(t.process.nuevoInvolucrado);
                  if (v) setInvolucrados([...involucrados, v]);
                }}
                style={{
                  ...CHIP,
                  cursor: 'pointer',
                  background: `${C.azul2}08`,
                  border: `1px dashed ${C.azul2}40`,
                }}
              >
                <Ic d={ICON_PATHS.plus} s={12} c={C.azul2} /> {t.process.anadir}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ═══ CARD 2: Descripción ═══
  const card2 = (
    <div key="descripcion" style={CARD}>
      <div style={HEADER_WITH_ICON}>
        {SECTION_ICONS.descripcion}
        {t.process.descripcionSeccion}
      </div>
      <div style={CARD_BODY}>
        {isEditing ? (
          <>
            <div style={{ ...LABEL, marginBottom: 4 }}>{t.common.descripcion}</div>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              style={TEXTAREA_STYLE}
              placeholder={t.common.processDescription}
            />
            <div style={{ ...LABEL, marginTop: 12, marginBottom: 4 }}>{t.process.alcance}</div>
            <textarea
              value={alcance}
              onChange={(e) => setAlcance(e.target.value)}
              style={TEXTAREA_STYLE}
              placeholder={t.common.processScope}
            />
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6, margin: 0 }}>
              {descripcion || t.process.sinDescripcionCorto}
            </p>
            <div style={{ marginTop: 12 }}>
              <div style={{ ...LABEL, marginBottom: 4 }}>{t.process.alcance}</div>
              <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.5, margin: 0 }}>
                {alcance}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // ═══ CARD 3: Entradas y Salidas ═══
  const card3 = (
    <div key="entradas-salidas" style={CARD}>
      <div style={HEADER_WITH_ICON}>
        {SECTION_ICONS.entradas}
        {t.process.entradasSalidas}
      </div>
      <div style={{ ...CARD_BODY, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ ...LABEL, marginBottom: 8, color: C.azul2 }}>{t.process.entradas}</div>
          <IOList
            items={entradas}
            setItems={setEntradas}
            borderColor={C.azul2}
            dirLabel={t.process.origen}
          />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ ...LABEL, marginBottom: 8, color: '#16A34A' }}>{t.process.salidas}</div>
          <IOList
            items={salidas}
            setItems={setSalidas}
            borderColor="#16A34A"
            dirLabel={t.process.destino}
          />
        </div>
      </div>
    </div>
  );

  // ═══ CARD 4: Reportes y Requisitos ═══
  const card4 = (
    <div key="reportes-requisitos" style={CARD}>
      <div style={HEADER_WITH_ICON}>
        {SECTION_ICONS.reportes}
        {t.process.reportesRequisitos}
      </div>
      <div style={{ ...CARD_BODY, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ ...LABEL, marginBottom: 8 }}>{t.process.reportes}</div>
          {reportes.map((r, i) => (
            <EditableListItem
              key={i}
              text={r}
              onRemove={() => setReportes(reportes.filter((_, j) => j !== i))}
            />
          ))}
          <AddButton
            onAdd={() => {
              const v = prompt(t.process.nuevoReporte);
              if (v) setReportes([...reportes, v]);
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ ...LABEL, marginBottom: 8 }}>{t.process.requisitos}</div>
          {requisitos.map((r, i) => (
            <EditableListItem
              key={i}
              text={r}
              onRemove={() => setRequisitos(requisitos.filter((_, j) => j !== i))}
            />
          ))}
          <AddButton
            onAdd={() => {
              const v = prompt(t.process.nuevoRequisito);
              if (v) setRequisitos([...requisitos, v]);
            }}
          />
        </div>
      </div>
    </div>
  );

  // ═══ CARD 5: Documentación (with draft workflow) ═══
  const card5 = (
    <div key="documentacion" style={CARD}>
      <div style={HEADER_WITH_ICON}>
        {SECTION_ICONS.documentacion}
        {t.process.documentacion}
        {documentos.length + draftDocs.length > 0 && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 10,
              fontWeight: 700,
              color: '#fff',
              padding: '1px 8px',
              borderRadius: 10,
              background: 'linear-gradient(135deg, #33BCE1, #2F5496)',
            }}
          >
            {documentos.length}
            {draftDocs.length > 0 ? ` + ${draftDocs.length}` : ''}
          </span>
        )}
      </div>
      <div style={CARD_BODY}>
        {/* Real documents from API */}
        {loadingDocs ? (
          <div style={{ padding: '8px 0' }}>
            <LoadingSpinner text={t.process.cargandoDocumentos} size="sm" />
          </div>
        ) : documentos.length === 0 && draftDocs.length === 0 && !showAddForm ? (
          <div style={{ fontSize: 13, color: C.textMut, padding: '8px 0' }}>
            {t.process.sinDocumentos}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {documentos.map((doc) => {
              const edLabel = doc.edicionActual
                ? `Ed.${String(doc.edicionActual).padStart(2, '0')}`
                : '';
              return (
                <div key={doc.id}>
                  <div
                    onClick={() => {
                      setSelectedDocId(doc.id);
                      onSelectDocumento?.(doc);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '9px 12px',
                      borderRadius: 9,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      border: `1px solid ${selectedDocId === doc.id ? 'rgba(51,188,225,0.25)' : 'transparent'}`,
                      background:
                        selectedDocId === doc.id ? 'rgba(51,188,225,0.10)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedDocId !== doc.id) {
                        e.currentTarget.style.background = 'rgba(248,249,252,0.8)';
                        e.currentTarget.style.borderColor = '#EAECF0';
                      }
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.03)';
                      e.currentTarget.style.transform = 'translateY(-0.5px)';
                    }}
                    onMouseLeave={(e) => {
                      if (selectedDocId !== doc.id) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = 'transparent';
                      }
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'none';
                    }}
                  >
                    <DocTypeBadge type={doc.tipoDocumental} />
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 10.5,
                        fontWeight: 500,
                        color: '#98A2B3',
                        minWidth: 80,
                        flexShrink: 0,
                      }}
                    >
                      {doc.codigo}
                    </span>
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 400,
                        color: '#101828',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {doc.titulo}
                    </span>
                    {edLabel && (
                      <span
                        style={{
                          fontSize: 9.5,
                          color: '#D0D5DD',
                          fontFamily: "'JetBrains Mono', monospace",
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        {edLabel}
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedArchivosDocId((prev) => (prev === doc.id ? null : doc.id));
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px 4px',
                        opacity: expandedArchivosDocId === doc.id ? 1 : 0.4,
                        transition: 'opacity 0.15s',
                        flexShrink: 0,
                      }}
                      title={t.archivos.tabArchivos}
                    >
                      <Ic d={ICON_PATHS.clip} s={13} c="#98A2B3" />
                    </button>
                    <StatusBadge status={doc.estado} small />
                  </div>
                  {expandedArchivosDocId === doc.id && (
                    <div
                      style={{ padding: '4px 10px 12px 10px', borderBottom: '1px solid #EAECF0' }}
                    >
                      <ArchivosPanel codigoDocumento={doc.codigo} readOnly={!isEditing} />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Draft documents (pending creation) */}
            {draftDocs.map((draft) => (
              <div
                key={draft.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: `1px dashed ${C.border}`,
                  marginTop: 2,
                  background: `${C.bg}80`,
                }}
              >
                <DocTypeBadge type={draft.tipo} />
                <span
                  style={{
                    fontSize: 13,
                    color: C.textSec,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {draft.nombre}
                </span>
                <span
                  style={{ fontSize: 11, fontStyle: 'italic', color: C.textMut, flexShrink: 0 }}
                >
                  {t.process.pendiente}
                </span>
                <button
                  onClick={() => handleCreateDoc(draft)}
                  disabled={creatingDocId === draft.id}
                  style={{
                    ...BTN_PRIMARY,
                    padding: '3px 10px',
                    fontSize: 11,
                    opacity: creatingDocId === draft.id ? 0.6 : 1,
                  }}
                >
                  {creatingDocId === draft.id ? t.process.creando : t.process.crearDocumento}
                </button>
                <button
                  onClick={() => handleRemoveDraft(draft.id)}
                  style={{ ...BTN_SMALL, width: 20, height: 20 }}
                  title={t.process.eliminarBorrador}
                >
                  <Ic d={ICON_PATHS.x} s={12} c={C.textMut} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Inline add form */}
        {showAddForm ? (
          <div
            style={{
              marginTop: 10,
              padding: 12,
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: C.bg,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {/* Tipo de contenido selector */}
            <div>
              <div style={{ ...LABEL, marginBottom: 6 }}>{t.archivos.filtroTipoContenido}</div>
              <div style={{ display: 'flex', gap: 0 }}>
                {[
                  {
                    value: 'DIGITAL' as TipoContenido,
                    label: t.archivos.digital,
                    icon: '\uD83D\uDCC4',
                  },
                  {
                    value: 'ARCHIVO' as TipoContenido,
                    label: t.archivos.archivo,
                    icon: '\uD83D\uDCE6',
                  },
                  {
                    value: 'ENLACE_EXTERNO' as TipoContenido,
                    label: t.archivos.enlaceExterno,
                    icon: '\uD83D\uDD17',
                  },
                ].map((opt, idx) => {
                  const isActive = newDocTipoContenido === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setNewDocTipoContenido(opt.value)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '7px 14px',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: FONTS.ui,
                        border: `1px solid ${isActive ? '#0E243E' : '#d5d5d5'}`,
                        borderLeft: idx > 0 ? 'none' : undefined,
                        borderRadius: idx === 0 ? '6px 0 0 6px' : idx === 2 ? '0 6px 6px 0' : 0,
                        background: isActive ? '#F0F2F5' : '#fff',
                        color: isActive ? '#0E243E' : C.textSec,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ fontSize: 14 }}>{opt.icon}</span>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div style={{ ...LABEL, marginBottom: 4 }}>{t.common.documentName}</div>
              <input
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                placeholder={t.common.examplePolicy}
                style={INPUT_STYLE}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newDocName.trim()) handleAddDraft();
                }}
              />
            </div>
            <div>
              <div style={{ ...LABEL, marginBottom: 4 }}>{t.process.tipoDocumental}</div>
              <select
                value={newDocTipo}
                onChange={(e) => setNewDocTipo(e.target.value as TipoDocumental)}
                style={{ ...INPUT_STYLE, cursor: 'pointer' }}
              >
                {TIPO_DOCUMENTAL_CODES.map((code) => (
                  <option key={code} value={code}>
                    {code} — {t.docTypes[code]}
                  </option>
                ))}
              </select>
            </div>

            {/* ARCHIVO: file input */}
            {newDocTipoContenido === 'ARCHIVO' && (
              <div>
                <div style={{ ...LABEL, marginBottom: 4 }}>{t.archivos.seleccionarArchivo}</div>
                <input
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                  style={{ ...INPUT_STYLE, padding: '4px 10px' }}
                />
                {selectedFile && (
                  <div style={{ fontSize: 11, color: C.textMut, marginTop: 4 }}>
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
              </div>
            )}

            {/* ENLACE_EXTERNO: URL + mimeType inputs */}
            {newDocTipoContenido === 'ENLACE_EXTERNO' && (
              <>
                <div>
                  <div style={{ ...LABEL, marginBottom: 4 }}>{t.archivos.urlEnlace}</div>
                  <input
                    value={enlaceUrl}
                    onChange={(e) => setEnlaceUrl(e.target.value)}
                    placeholder="https://..."
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <div style={{ ...LABEL, marginBottom: 4 }}>{t.archivos.tipoMime}</div>
                  <select
                    value={enlaceMime}
                    onChange={(e) => setEnlaceMime(e.target.value)}
                    style={{ ...INPUT_STYLE, cursor: 'pointer' }}
                  >
                    <option value="application/pdf">PDF</option>
                    <option value="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">
                      Excel (.xlsx)
                    </option>
                    <option value="application/vnd.openxmlformats-officedocument.wordprocessingml.document">
                      Word (.docx)
                    </option>
                    <option value="application/vnd.openxmlformats-officedocument.presentationml.presentation">
                      PowerPoint (.pptx)
                    </option>
                    <option value="text/html">HTML</option>
                    <option value="image/png">PNG</option>
                    <option value="image/jpeg">JPEG</option>
                  </select>
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleAddDraft}
                disabled={!newDocName.trim()}
                style={{
                  ...BTN_PRIMARY,
                  opacity: newDocName.trim() ? 1 : 0.5,
                }}
              >
                <Ic d={ICON_PATHS.plus} s={14} c="#fff" />
                {t.process.anadirALista}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewDocName('');
                  setNewDocTipo('NC');
                  setNewDocTipoContenido('DIGITAL');
                  setSelectedFile(null);
                  setEnlaceUrl('');
                  setEnlaceMime('application/pdf');
                }}
                style={BTN_OUTLINE}
              >
                {t.common.cancelar}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            style={{ ...BTN_OUTLINE, marginTop: 10, fontSize: 12 }}
          >
            <Ic d={ICON_PATHS.plus} s={14} c={C.textSec} />
            {t.process.anadirDocumento}
          </button>
        )}
      </div>
    </div>
  );

  // ═══ CARD 6: Herramientas ═══
  const card6 = (
    <div key="herramientas" style={CARD}>
      <div style={HEADER_WITH_ICON}>
        {SECTION_ICONS.herramientas}
        {t.process.herramientasSeccion}
      </div>
      <div style={CARD_BODY}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {herramientas.map((h, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                background: C.bg,
                color: C.text,
                border: `1px solid ${C.border}`,
              }}
            >
              <Ic d={ICON_PATHS.tool} s={14} c={C.textMut} />
              {h}
              {isEditing && (
                <button
                  onClick={() => setHerramientas(herramientas.filter((_, j) => j !== i))}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    marginLeft: 2,
                  }}
                >
                  <Ic d={ICON_PATHS.x} s={12} c={C.textMut} />
                </button>
              )}
            </span>
          ))}
        </div>
        <AddButton
          onAdd={() => {
            const v = prompt(t.process.nuevaHerramienta);
            if (v) setHerramientas([...herramientas, v]);
          }}
        />
      </div>
    </div>
  );

  // ═══ CARD 7: Indicadores ═══
  const card7 = (
    <div key="indicadores" style={CARD}>
      <div style={HEADER_WITH_ICON}>
        {SECTION_ICONS.indicadores}
        {t.process.indicadoresSeccion}
      </div>
      <div style={CARD_BODY}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {indicadores.map((ind, i) => (
            <div
              key={i}
              style={{
                padding: 14,
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: C.bg,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 10,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  {isEditing ? (
                    <input
                      value={ind.nombre}
                      onChange={(e) => {
                        const u = [...indicadores];
                        u[i] = { ...u[i], nombre: e.target.value };
                        setIndicadores(u);
                      }}
                      style={{ ...INPUT_STYLE, fontWeight: 600 }}
                    />
                  ) : (
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{ind.nombre}</div>
                  )}
                </div>
                {isEditing && (
                  <button
                    onClick={() => setIndicadores(indicadores.filter((_, j) => j !== i))}
                    style={BTN_SMALL}
                    title={t.common.eliminar}
                  >
                    <Ic d={ICON_PATHS.x} s={14} c={C.textMut} />
                  </button>
                )}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div>
                  <div style={{ ...LABEL, fontSize: 10 }}>{t.processSheet.fields.objective}</div>
                  {isEditing ? (
                    <input
                      value={ind.objetivo}
                      onChange={(e) => {
                        const u = [...indicadores];
                        u[i] = { ...u[i], objetivo: e.target.value };
                        setIndicadores(u);
                      }}
                      style={{ ...INPUT_STYLE, fontSize: 12 }}
                    />
                  ) : (
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>
                      {ind.objetivo}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ ...LABEL, fontSize: 10 }}>{t.processSheet.fields.frequency}</div>
                  {isEditing ? (
                    <select
                      value={ind.frecuencia}
                      onChange={(e) => {
                        const u = [...indicadores];
                        u[i] = { ...u[i], frecuencia: e.target.value };
                        setIndicadores(u);
                      }}
                      style={{ ...INPUT_STYLE, fontSize: 12, cursor: 'pointer' }}
                    >
                      <option value="Mensual">{t.processSheet.freq.mensual}</option>
                      <option value="Trimestral">{t.processSheet.freq.trimestral}</option>
                      <option value="Semestral">{t.processSheet.freq.semestral}</option>
                      <option value="Anual">{t.processSheet.freq.anual}</option>
                    </select>
                  ) : (
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>
                      {ind.frecuencia}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ ...LABEL, fontSize: 10 }}>{t.process.valorActual}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.azul2 }}>{ind.valor}%</div>
                </div>
              </div>
              <div
                style={{
                  height: 8,
                  borderRadius: 4,
                  background: C.border,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(ind.valor, 100)}%`,
                    borderRadius: 4,
                    background: `linear-gradient(90deg, ${C.azul2}, ${C.celeste})`,
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
              {isEditing && (
                <div style={{ marginTop: 8 }}>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={ind.valor}
                    onChange={(e) => {
                      const u = [...indicadores];
                      u[i] = { ...u[i], valor: Number(e.target.value) };
                      setIndicadores(u);
                    }}
                    style={{ width: '100%', accentColor: C.azul2 }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {isEditing && (
          <button
            onClick={() =>
              setIndicadores([
                ...indicadores,
                {
                  nombre: t.process.nuevoIndicador,
                  objetivo: '≥ 80%',
                  frecuencia: 'Mensual',
                  valor: 0,
                  meta: 100,
                },
              ])
            }
            style={{ ...BTN_OUTLINE, marginTop: 12, fontSize: 12 }}
          >
            <Ic d={ICON_PATHS.plus} s={14} c={C.textSec} /> {t.process.anadirIndicador}
          </button>
        )}
      </div>
    </div>
  );

  // ─── Column distribution ───
  const leftCards = [card1, card2, card3, card4];
  const rightCards = [card5, card6, card7];

  return (
    <div
      style={{
        width: '100%',
        fontFamily: FONTS.ui,
        padding: 16,
      }}
    >
      {/* ═══ ACTION BAR — only when NOT using lifted edit state ═══ */}
      {!hasLiftedEdit && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
            justifyContent: 'flex-end',
          }}
        >
          {isEditing ? (
            <>
              <button onClick={handleSave} style={BTN_PRIMARY}>
                <Ic d={ICON_PATHS.save} s={14} c="#fff" />
                {t.common.guardar}
              </button>
              <button onClick={handleCancel} style={BTN_OUTLINE}>
                {t.common.cancelar}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setInternalIsEditing(true)} style={BTN_OUTLINE}>
                <Ic d={ICON_PATHS.edit} s={14} c={C.textSec} />
                {t.common.editar}
              </button>
              <button onClick={() => alert(t.process.exportPdfPendiente)} style={BTN_OUTLINE}>
                <Ic d={ICON_PATHS.download} s={14} c={C.textSec} />
                {t.process.exportarPdf}
              </button>
            </>
          )}
        </div>
      )}

      {/* ═══ CARDS — 2-column, single column, or docs-only ═══ */}
      {showOnlyDocs ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{rightCards}</div>
      ) : twoColumnLayout ? (
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{leftCards}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{rightCards}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[...leftCards, ...rightCards]}
        </div>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
}
